import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import ArticleCard from '../components/ArticleCard';
import CategoryBanner from '../components/CategoryBanner';

const CATEGORY_LABELS = {
  technology:    'Technology',
  finance:       'Finance',
  india_finance: 'India Finance',
  fashion:       'Fashion',
  sports:        'Sports',
  health:        'Health & Fitness',
  education:     'Education',
  space:         'Space',
  science:       'Science & Research',
};

function SkeletonCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden animate-pulse">
      <div className="h-1 bg-gray-200 w-full" />
      <div className="p-5">
        <div className="h-3.5 bg-gray-200 rounded w-full mb-2" />
        <div className="h-3.5 bg-gray-200 rounded w-4/5 mb-4" />
        <div className="h-2.5 bg-gray-100 rounded w-full mb-1.5" />
        <div className="h-2.5 bg-gray-100 rounded w-3/4" />
      </div>
      <div className="px-5 pb-4 flex justify-between">
        <div className="h-5 bg-gray-100 rounded-md w-20" />
        <div className="h-5 bg-gray-100 rounded w-16" />
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { category }            = useParams();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  useEffect(() => {
    if (!category) return;
    setLoading(true);
    setError('');
    setArticles([]);

    supabase
      .from('articles_ranked')
      .select('id, title, description, source_url, published_at, fetched_at, view_count')
      .eq('category', category)
      .order('relevance_score', { ascending: false })
      .limit(30)
      .then(({ data, error }) => {
        if (error) setError('Could not load articles. Please try again.');
        else setArticles(data ?? []);
      })
      .finally(() => setLoading(false));
  }, [category]);

  const label = CATEGORY_LABELS[category] ?? category;

  return (
    <div>
      <CategoryBanner
        category={category}
        label={label}
        articleCount={loading ? 0 : articles.length}
      />

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-fade-in-up" style={{ animationDelay: `${i * 0.02}s` }}>
              <SkeletonCard />
            </div>
          ))}
        </div>

      ) : error ? (
        <div className="flex items-center justify-center py-20">
          <div className="bg-red-50 border border-red-200 rounded-xl p-8 text-center max-w-xs">
            <p className="text-4xl mb-3">⚠️</p>
            <p className="text-red-700 font-medium">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-800 rounded-lg text-sm font-medium transition-colors"
            >
              Retry
            </button>
          </div>
        </div>

      ) : articles.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <p className="text-6xl mb-4">📭</p>
          <p className="text-lg font-medium text-gray-500">No articles yet</p>
          <p className="text-sm mt-1">The background worker fetches new content every 2 hours.</p>
        </div>

      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {articles.map((article, i) => (
            <div
              key={article.id}
              className="animate-fade-in-up h-full"
              style={{ animationDelay: `${Math.min(i * 0.03, 0.18)}s` }}
            >
              <ArticleCard article={article} category={category} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
